/**
 * Pure calculator for the signed campaign action-dispatch ceiling.
 *
 * The reserve covers campaign bookkeeping/remediation dispatches. Workflow
 * blocks group adjacent actions that operate over the same bounded population.
 */

export const CAMPAIGN_STEP_RESERVE = 10;
export const DEFAULT_DOCK_POSE_UPPER_BOUND = 10;
export const GROW_PRODUCT_UPPER_BOUND = 12;
export const MAX_CAMPAIGN_STEP_CAP = 1_000_000;

function checkedInteger(value, field, { minimum = 0 } = {}) {
    if (!Number.isSafeInteger(value) || value < minimum) {
        throw new Error(`${field} must be a safe integer >= ${minimum}`);
    }
    return value;
}

function checkedProduct(left, right, field) {
    const product = left * right;
    if (!Number.isSafeInteger(product) || product > MAX_CAMPAIGN_STEP_CAP) {
        throw new Error(`${field} exceeds the campaign step-cap limit`);
    }
    return product;
}

function checkedSum(left, right, field) {
    const sum = left + right;
    if (!Number.isSafeInteger(sum) || sum > MAX_CAMPAIGN_STEP_CAP) {
        throw new Error(`${field} exceeds the campaign step-cap limit`);
    }
    return sum;
}

export function calculateCampaignStepBudget(blocks, {
    reserve = CAMPAIGN_STEP_RESERVE,
} = {}) {
    if (!Array.isArray(blocks) || blocks.length === 0) {
        throw new Error('campaign step budget requires at least one workflow block');
    }
    checkedInteger(reserve, 'reserve');
    if (reserve !== CAMPAIGN_STEP_RESERVE) {
        throw new Error(`reserve must equal the fixed ${CAMPAIGN_STEP_RESERVE}-dispatch allowance`);
    }
    let total = reserve;
    const normalizedBlocks = blocks.map((block, index) => {
        const stepsPerMolecule = checkedInteger(
            block?.stepsPerMolecule,
            `blocks[${index}].stepsPerMolecule`,
            { minimum: 1 },
        );
        const moleculeCount = checkedInteger(
            block?.moleculeCount,
            `blocks[${index}].moleculeCount`,
            { minimum: 1 },
        );
        const actionIds = Array.isArray(block?.actionIds)
            ? block.actionIds.map((actionId) => String(actionId))
            : [];
        if (actionIds.length !== stepsPerMolecule || actionIds.some((actionId) => !actionId)) {
            throw new Error(`blocks[${index}].actionIds must identify every expected step`);
        }
        if (typeof block.moleculeCountSource !== 'string' || !block.moleculeCountSource) {
            throw new Error(`blocks[${index}].moleculeCountSource is required`);
        }
        const subtotal = checkedProduct(
            stepsPerMolecule,
            moleculeCount,
            `blocks[${index}] subtotal`,
        );
        total = checkedSum(total, subtotal, 'campaign step budget');
        return {
            id: `block-${index + 1}`,
            actionIds,
            stepsPerMolecule,
            moleculeCount,
            moleculeCountSource: block.moleculeCountSource,
            subtotal,
        };
    });
    return {
        version: 1,
        formula: '10 + sum(stepsPerMolecule * moleculeCount)',
        reserve,
        upperBounds: {
            defaultDockPosesPerMolecule: DEFAULT_DOCK_POSE_UPPER_BOUND,
            growProductsPerAction: GROW_PRODUCT_UPPER_BOUND,
        },
        blocks: normalizedBlocks,
        total,
    };
}

function appendBlock(blocks, actionId, moleculeCount, moleculeCountSource) {
    const previous = blocks[blocks.length - 1];
    if (previous
        && previous.moleculeCount === moleculeCount
        && previous.moleculeCountSource === moleculeCountSource) {
        previous.actionIds.push(actionId);
        previous.stepsPerMolecule += 1;
        return;
    }
    blocks.push({
        actionIds: [actionId],
        stepsPerMolecule: 1,
        moleculeCount,
        moleculeCountSource,
    });
}

export function deriveWorkflowStepBudget(steps, workspaceBaseline, workflow = {}) {
    if (!Array.isArray(steps) || steps.length === 0) {
        throw new Error('campaign step budget requires at least one submitted step');
    }
    const baselineCount = checkedInteger(
        workspaceBaseline?.compoundIds?.length,
        'workspaceBaseline.compoundIds.length',
        { minimum: 1 },
    );
    let moleculeCount = baselineCount;
    let moleculeCountSource = 'signed-baseline-compounds';
    const blocks = [];
    const canonicalNativeWorkflow = workflow?.id === 'hit-to-lead'
        && workflow?.source === 'campaign-verb-shortcut'
        && workspaceBaseline?.startingStateClassification?.kind === 'native-reference-only';
    let growPopulation = null;

    steps.forEach((step, index) => {
        const actionId = typeof step?.actionId === 'string' ? step.actionId : '';
        if (!actionId) throw new Error(`steps[${index}].actionId is required`);
        const identityStep = canonicalNativeWorkflow
            && ['predict-admet', 'export-compounds'].includes(actionId);
        const stepMoleculeCount = identityStep
            ? baselineCount + (growPopulation || 0)
            : moleculeCount;
        let stepMoleculeCountSource = moleculeCountSource;
        if (identityStep) {
            stepMoleculeCountSource = growPopulation
                ? 'signed-native-and-grown-upper-bound'
                : 'signed-native-identity-compounds';
        }
        appendBlock(blocks, actionId, stepMoleculeCount, stepMoleculeCountSource);

        if (actionId === 'dock-compound') {
            const reviewedPoseCount = step.params?.poseCount;
            const poseUpperBound = reviewedPoseCount === undefined
                ? DEFAULT_DOCK_POSE_UPPER_BOUND
                : checkedInteger(reviewedPoseCount, `steps[${index}].params.poseCount`, { minimum: 1 });
            moleculeCount = checkedProduct(
                moleculeCount,
                poseUpperBound,
                `steps[${index}] dock population`,
            );
            moleculeCountSource = reviewedPoseCount === undefined
                ? 'signed-default-dock-pose-upper-bound'
                : 'signed-reviewed-dock-pose-upper-bound';
        } else if (actionId === 'grow-from-atom') {
            moleculeCount = GROW_PRODUCT_UPPER_BOUND;
            growPopulation = moleculeCount;
            moleculeCountSource = 'signed-grow-product-upper-bound';
        }
    });

    return calculateCampaignStepBudget(blocks);
}
